import { useEffect, useState } from 'react';

import { api } from '../api.ts';

/**
 * Kézi számlarögzítés. (Megbízás 2.0 / 5.4)
 *
 * Akkor kell, ha egy számla nem a napi exportból jön — például egy
 * gyűjtőszámla, amit külön állítottak ki. A telephelyet kereséssel
 * választjuk ki, hogy a számla a megfelelő céghez kerüljön.
 */
export function UjSzamla({ onKesz, onMegse }: { onKesz: () => void; onMegse: () => void }) {
  const [bizonylatszam, setBizonylatszam] = useState('');
  const [kelt, setKelt] = useState(new Date().toISOString().slice(0, 10));
  const [hatarido, setHatarido] = useState('');
  const [brutto, setBrutto] = useState('');
  const [fizetesiMod, setFizetesiMod] = useState('Átutalás');
  const [megjegyzes, setMegjegyzes] = useState('');

  const [telephelyKereses, setTelephelyKereses] = useState('');
  const [talalatok, setTalalatok] = useState<
    Array<{ id: number; nev: string; cim: string | null; ceg_nev: string | null }>
  >([]);
  const [valasztott, setValasztott] = useState<{ id: number; nev: string; ceg_nev: string | null } | null>(
    null,
  );

  const [hiba, setHiba] = useState<string | null>(null);
  const [munkaban, setMunkaban] = useState(false);

  useEffect(() => {
    if (valasztott || telephelyKereses.trim().length < 2) {
      setTalalatok([]);
      return;
    }
    const t = setTimeout(() => {
      api
        .get<{ talalatok: typeof talalatok }>(
          `/api/szamla/telephely?q=${encodeURIComponent(telephelyKereses.trim())}`,
        )
        .then((v) => setTalalatok(v.talalatok))
        .catch(() => setTalalatok([]));
    }, 220);
    return () => clearTimeout(t);
  }, [telephelyKereses, valasztott]);

  async function felvesz() {
    setMunkaban(true);
    setHiba(null);
    try {
      await api.post('/api/szamla', {
        bizonylatszam,
        kelt,
        fizetesi_hatarido: hatarido,
        brutto,
        fizetesi_mod: fizetesiMod,
        megjegyzes,
        etterem_id: valasztott?.id ?? null,
        cegnev: valasztott?.ceg_nev ?? null,
      });
      onKesz();
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A rögzítés nem sikerült.');
      setMunkaban(false);
    }
  }

  return (
    <div className="tdet" style={{ background: '#fff', borderColor: 'var(--rule2)' }}>
      <h4>Számla kézi rögzítése</h4>
      {hiba && <div className="uzenet bad">{hiba}</div>}

      <div className="qgrid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <label>
          Bizonylatszám
          <input
            value={bizonylatszam}
            onChange={(e) => setBizonylatszam(e.target.value)}
            placeholder="pl. 2/1808"
          />
        </label>
        <label>
          Kelt
          <input value={kelt} onChange={(e) => setKelt(e.target.value)} placeholder="ÉÉÉÉ-HH-NN" />
        </label>
        <label>
          Fizetési határidő
          <input
            value={hatarido}
            onChange={(e) => setHatarido(e.target.value)}
            placeholder="ÉÉÉÉ-HH-NN"
          />
        </label>
        <label>
          Bruttó
          <input value={brutto} onChange={(e) => setBrutto(e.target.value)} />
        </label>
      </div>

      <div className="qgrid" style={{ gridTemplateColumns: '1fr 2fr' }}>
        <label>
          Fizetési mód
          <select value={fizetesiMod} onChange={(e) => setFizetesiMod(e.target.value)}>
            <option>Átutalás</option>
            <option>Készpénz</option>
            <option>Bankkártya</option>
          </select>
        </label>
        <label>
          Megjegyzés
          <input
            value={megjegyzes}
            onChange={(e) => setMegjegyzes(e.target.value)}
            style={{ textAlign: 'left', fontFamily: 'inherit' }}
          />
        </label>
      </div>

      <p className="lbl" style={{ marginTop: 12 }}>
        Telephely
      </p>
      {valasztott ? (
        <div className="uzenet ok" style={{ marginTop: 0 }}>
          <b>{valasztott.nev}</b>
          {valasztott.ceg_nev && ` · ${valasztott.ceg_nev}`}
          <button
            className="btn sm"
            style={{ marginLeft: 12 }}
            onClick={() => {
              setValasztott(null);
              setTelephelyKereses('');
            }}
          >
            Másikat
          </button>
        </div>
      ) : (
        <>
          <input
            className="qi"
            style={{ width: 320, fontSize: 12.5 }}
            placeholder="étterem vagy cégnév keresése…"
            value={telephelyKereses}
            onChange={(e) => setTelephelyKereses(e.target.value)}
          />
          {talalatok.length > 0 && (
            <div className="matrix" style={{ maxHeight: 170, overflow: 'auto', marginTop: 8 }}>
              <table className="tt2">
                <tbody>
                  {talalatok.map((t) => (
                    <tr key={t.id} style={{ cursor: 'pointer' }} onClick={() => setValasztott(t)}>
                      <td className="nm3">
                        <b>{t.nev}</b>
                        {t.ceg_nev && <small>{t.ceg_nev}</small>}
                      </td>
                      <td style={{ padding: '6px 10px', fontSize: 11.5 }}>{t.cim ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="hint" style={{ marginTop: 6 }}>
            Telephely nélkül is rögzíthető — utólag a listában is hozzárendelhető.
          </p>
        </>
      )}

      <div className="btns">
        <button
          className="btn pri sm"
          disabled={munkaban || !bizonylatszam.trim() || !brutto.trim()}
          onClick={() => void felvesz()}
        >
          Rögzítés
        </button>
        <button className="btn sm" onClick={onMegse}>
          Mégsem
        </button>
      </div>
    </div>
  );
}
